import { UIMemo } from '../../model/UiShapes';
import { DataStatus } from '../../common/MetaData';
import { syncMemo, fetchMemo } from '../../hooks/api/memoAPI';
import { fetchMemoViewByMemoId } from '../../home/components/memoView/memoViewAPI';
import { MemoState } from '../memoStore';
import { handleError, useErrorHandler } from '../../hooks/managers/errorHandler';
import { DEFAULT_GROUP_GUID } from '../../common/MetaSetting';

/**
 * Memo Store同步Actions接口
 */
export interface MemoStoreActions {
  setMemos: (memos: UIMemo[]) => void;
  addMemo: (memo: UIMemo) => void;
  updateMemo: (memo: UIMemo) => void;
  setSelectedMemoId: (memoId: string) => void;
  setCurrentMemo: (memo: UIMemo | null) => void;
  setEditTitle: (title: string) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setIsSyncing: (syncing: boolean) => void;
  addFailedMemoUpdate: (record: any) => void;
  clearFailedMemoUpdates: () => void;
  clear: () => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setCurrentViewMemoStatus: (status: number) => void;
  setCurrentViewMemo: (memo: UIMemo | undefined) => void;
  setSearchKey: (searchKey: string) => void;
  setSearchMemos: (searchMemos: UIMemo[]) => void;
}

/**
 * Memo Store异步Actions接口
 */
export interface MemoAsyncActions {
  updateMemoAsync: (param: { memo: UIMemo }) => Promise<void>;
  updateMemoStatusByIdAsync: (params: { memoId: string; updateStatus: number }) => Promise<void>;
  transferToDefaultByGroupId: (groupId: string) => Promise<void>;
  fetchViewMemoAsync: (params: { memoId: string }) => Promise<void>;
  searchByKeyAsync: (searchKey: string) => Promise<number>;
}

/**
 * Memo Store Actions Hook - 用于在组件中访问store actions（依赖注入版本）
 */
export const createMemoStoreActions = (set: Function, get: Function) => {
  // 从store实例中获取actions
  const actions = {
    setMemos: (memos: UIMemo[]) => set({ memos }),
    addMemo: (memo: UIMemo) => set((state: MemoState) => ({
      memos: [...state.memos.filter((m) => m.guid !== memo.guid), memo],
      currentMemo: state.currentMemo?.guid === memo.guid ? memo : state.currentMemo
    })),
    updateMemo: (memo: UIMemo) => set((state: MemoState) => ({
      memos: state.memos.map((m) => (m.guid === memo.guid ? memo : m)),
      currentMemo: state.currentMemo?.guid === memo.guid ? memo : state.currentMemo
    })),
    setSelectedMemoId: (memoId: string) => {
      set({ currentMemoId: memoId });
      const state = get();
      const memo = state.memos.find((m: UIMemo) => m.guid === memoId);
      set({ currentMemo: memo });
      if (memo && memo.title) {
        set({ editTitle: memo.title });
      } else {
        set({ editTitle: '' });
      }
    },
    setEditTitle: (title: string) => set({ editTitle: title }),
    setStatus: (status: 'idle' | 'loading' | 'failed') => set({ status }),
    setIsSyncing: (syncing: boolean) => set({ isSyncing: syncing }),
    addFailedMemoUpdate: (record: any) => set((state: MemoState) => ({
      failedMemoUpdateRecords: [...state.failedMemoUpdateRecords, record]
    })),
    clearFailedMemoUpdates: () => set({ failedMemoUpdateRecords: [] }),
    clear: () => set({
      memos: [],
      currentMemoId: '',
      currentMemo: null,
      status: 'idle',
      isSyncing: false,
      failedMemoUpdateRecords: [],
      currentViewMemoStatus: 0,
      currentViewMemo: undefined,
      searchKey: '',
      searchMemos: [],
      error: null
    }),
    setError: (error: string | null) => set({ error }),
    clearError: () => set({ error: null }),
    setCurrentViewMemoStatus: (status: number) => set({ currentViewMemoStatus: status }),
    setCurrentViewMemo: (memo: UIMemo | undefined) => set({ currentViewMemo: memo }),
    setSearchKey: (searchKey: string) => set({ searchKey }),
    setSearchMemos: (searchMemos: UIMemo[]) => set({ searchMemos })
  };

  return actions;
};

// 辅助函数：更新memo状态
const updateMemoWithStatus = (set: Function, memo: UIMemo, updateStatus: number) => {
  if (memo.guid) {
    set((state: MemoState) => ({
      memos: state.memos.map(m => 
        m.guid === memo.guid ? { ...memo, updateStatus } : m
      ),
      currentMemo: state.currentMemo?.guid === memo.guid ? 
        { ...memo, updateStatus } : state.currentMemo
    }));
  }
};

/**
 * Memo Store异步Actions实现（依赖注入版本）
 */
export const memoAsyncActions = (set: Function, get: Function): MemoAsyncActions & {
  fetchMemoAsync: (memoId: string) => Promise<{ content: UIMemo } | null>;
} => ({
  // 创建同步actions实例
  ...createMemoStoreActions(set, get),

  fetchMemoAsync: async (memoId: string) => {
    set({ status: 'loading' });
    try {
      // 调用API获取memo数据
      const response = await fetchMemo(memoId);
      
      // 如果获取到数据，更新store状态
      if (response?.content) {
        // 这里可以添加更新store的逻辑，如果需要的话
      }
      
      set({ status: 'idle' });
      
      // 返回数据给fetchQuery使用
      return response;
    } catch (error) {
      set({ status: 'failed' });
      throw error;
    }
  },



  updateMemoAsync: async (param: { memo: UIMemo }) => {
    const memo = param.memo;
    set({ status: 'loading' });
    try {
      // 首先将memo的updateStatus设置为UPDATING（触发sync操作时）
      updateMemoWithStatus(set, memo, DataStatus.UPDATING);

      // 直接调用API函数进行服务器请求（职责分离）
      // syncMemo 失败时抛错进入下方 catch（回滚 NOTUPDATE + 提示）
      const syncResponse = await syncMemo(memo);

      // 根据syncResult处理store更新
      if (syncResponse.syncResult === 1) {
        // syncResult为1：服务器已接受本次更新，将memo的updateStatus设置为UPDATED
        updateMemoWithStatus(set, memo, DataStatus.UPDATED);
      } else if (syncResponse.syncResult === -1) {
        // syncResult为-1：服务端版本更新（冲突）。必须回源拉取服务端最新数据回写 store，
        // 绝不能把本地过期数据标记为已同步（对照 memoShareActions 的 targetShare 回写模式）
        try {
          const fresh = await fetchMemo(memo.guid);
          if (fresh?.content) {
            updateMemoWithStatus(set, { ...fresh.content, updateStatus: DataStatus.UPDATED }, DataStatus.UPDATED);
          } else {
            // 服务端无该 memo（可能已被删除）：保留本地数据但回滚状态，提示用户
            updateMemoWithStatus(set, memo, DataStatus.NOTUPDATE);
            handleError(new Error('版本冲突且服务端已无该memo，请刷新列表'), { context: 'updateMemoAsync', showToast: true });
          }
        } catch (fetchError) {
          // 回源失败（弱网）：保留本地数据但回滚为未同步，避免过期数据被当作已同步
          updateMemoWithStatus(set, memo, DataStatus.NOTUPDATE);
          handleError(fetchError, { context: '版本冲突后重新获取memo失败', showToast: true });
        }
      } else {
        // 未知的 syncResult：按失败处理，回滚状态
        updateMemoWithStatus(set, memo, DataStatus.NOTUPDATE);
        handleError(new Error(`未知的syncResult: ${syncResponse.syncResult}`), { context: 'updateMemoAsync', showToast: true });
      }

      set({ status: 'idle' });
    } catch (error) {
      handleError(error, { context: '更新memo失败' });
      // 服务器请求失败时，将memo的updateStatus重置为NOTUPDATE
      updateMemoWithStatus(set, memo, DataStatus.NOTUPDATE);
      set({ status: 'failed' });
      throw error;
    }
  },

  updateMemoStatusByIdAsync: async ({ memoId, updateStatus }: { memoId: string; updateStatus: number }) => {
    set({ status: 'loading' });
    try {
      // 更新memo对象的updateStatus属性，同时更新modifyTimestamp和contentMark
      const currentTime = Date.now();
      set((state: MemoState) => ({
        memos: state.memos.map(m => 
          m.guid === memoId ? { 
            ...m, 
            updateStatus,
            modifyTimestamp: currentTime
          } : m
        ),
        currentMemo: state.currentMemo?.guid === memoId ? 
          { 
            ...state.currentMemo, 
            updateStatus,
            modifyTimestamp: currentTime
          } : state.currentMemo
      }));
      set({ status: 'idle' });
    } catch (error) {
      handleError(error, { context: 'updateMemoStatusAsync执行失败' });
      set({ status: 'failed' });
    }
  },

  transferToDefaultByGroupId: async (groupId: string) => {
    set({ status: 'loading', error: null });
    
    try {
      // 使用get函数获取当前store状态（依赖注入模式）
      const state = get();
      const memos = state.memos;
      
      // 筛选出groupId为指定值的memos
      const memosToTransfer = memos.filter((memo: UIMemo) => memo.groupId === groupId);
      
      if (memosToTransfer.length === 0) {
        set({ status: 'idle' });
        return;
      }
      
      // 为每个需要转移的memo更新groupId并调用syncMemo
      for (const memo of memosToTransfer) {
        const updatedMemo: UIMemo = {
          ...memo,
          groupId: DEFAULT_GROUP_GUID,
          modifyTimestamp: Date.now()
        };
        
        // 调用syncMemo进行同步
        const response = await syncMemo(updatedMemo);
        
        if (response.syncResult !== 1) {
          handleError(new Error('Memo同步失败'), { context: 'Memo同步失败' });
          throw new Error('Memo同步失败');
        }
        
        // 更新memo store状态（使用set函数）
        set((currentState: any) => ({
          memos: currentState.memos.map((m: UIMemo) => 
            m.guid === updatedMemo.guid ? updatedMemo : m
          )
        }));
      }
      
      set({ status: 'idle' });
    } catch (error) {
      set({ 
        status: 'failed', 
        error: error instanceof Error ? error.message : 'Failed to transfer memos to default group' 
      });
    }
  },

  /** @deprecated 使用 useMemoCrdt 获取CRDT实时内容，此方法仅获取元数据 */
  fetchViewMemoAsync: async ({ memoId }: { memoId: string }) => {
    set({ status: 'loading' });
    
    try {
      const response = await fetchMemoViewByMemoId(memoId);
      
      if (response.responseType === 1) {
        const content = await response.content;
        if (content) {
          set({ currentViewMemoStatus: content.requestResult, currentViewMemo: content.result });
        }
      } else {
        // Handle error with retry logic
        handleError(new Error(response.message), { context: '获取memo视图失败' });
        // TODO: Implement retry logic similar to Redux version
      }
      
      set({ status: 'idle' });
    } catch (error) {
      handleError(error, { context: 'fetchViewMemoAsync执行失败' });
      set({ status: 'failed' });
    }
  },
  
  searchByKeyAsync: async (searchKey: string) => {
    set({ status: 'loading' });
    try {
      const state = get();
      let filteredMemos: UIMemo[] = [];
      
      // 首先尝试使用Memo Indexes映射表进行搜索
      try {
        // 使用依赖注入模式：通过参数传递memoIndexStore状态
        // 这里需要外部传入memoIndexStore的状态，暂时回退到标题搜索
        console.warn('⚠️ Memo Indexes搜索功能暂时不可用，使用标题搜索');
        
        // 直接使用标题搜索逻辑
        filteredMemos = state.memos.filter((memo: UIMemo) => 
          memo.title.toLowerCase().includes(searchKey.toLowerCase())
        );
      } catch (error) {
        console.warn('❌ Memo Indexes映射表搜索失败，回退到原有搜索逻辑:', error);
        
        // 回退到标题搜索逻辑
        filteredMemos = state.memos.filter((memo: UIMemo) => 
          memo.title.toLowerCase().includes(searchKey.toLowerCase())
        );
      }
      
      set({ searchMemos: filteredMemos, status: 'idle' });
      return filteredMemos.length;
    } catch (error) {
      handleError(error, { context: '搜索memo失败' });
      set({ status: 'failed', searchMemos: [] });
      return 0;
    }
  },

});

// 为了向后兼容，保留旧的函数名（使用依赖注入模式）
export const memoStoreActions = (set: Function, get: Function) => createMemoStoreActions(set, get);